import { getLogger, type Logger } from "@expert/logging";
import { Channel } from "@expert/shared-types";
import ky, { HTTPError, type BeforeErrorHook, type BeforeRequestHook } from "ky";

import { env } from "@/config";

import { userCache } from "./userCache";

export function createAuthorizationBearerHook(tokenProvider: () => string | undefined): BeforeRequestHook {
    return ({ request: { headers } }) => {
        if (headers.has("Authorization")) return;
        const accessToken = tokenProvider();
        if (!accessToken) {
            throw new Error("Access token doesn’t exist");
        }
        headers.set("Authorization", `Bearer ${accessToken}`);
    };
}

export function createCorrelationIdHook(correlationId?: string): BeforeRequestHook {
    return ({ request: { headers } }) => {
        if (correlationId) headers.set("x-ewp-correlation-id", correlationId);
    };
}

export function createChannelHook(channel: Channel): BeforeRequestHook {
    return ({ request: { headers } }) => {
        headers.set("x-ewp-channel", channel);
    };
}

export function createErrorLoggingHook(logger: Logger): BeforeErrorHook {
    return ({ error }) => {
        if (!(error instanceof HTTPError)) {
            logger.warn({ error }, "received a non-HTTP error");
            return error;
        }
        const {
            request: { method, url },
            response: { status, statusText },
        } = error;
        logger.warn({ method, url, statusCode: status, statusText }, "received an HTTP Error");
        return error;
    };
}

export const getSharedApiClient = (correlationId?: string) => {
    return ky.create({
        prefix: env.backendUrls.apiBaseUrl,
        timeout: 30_000,
        hooks: {
            beforeRequest: [
                createAuthorizationBearerHook(() => userCache.ssoAccessToken),
                createCorrelationIdHook(correlationId),
                ...(!env.isProdMode ? [createChannelHook(env.channel)] : []),
            ],
            beforeError: [createErrorLoggingHook(getLogger({ module: "api" }))],
        },
    });
};
